import React from 'react';
import dynamic from 'next/dynamic'
import Head from 'next/head';
import { FAQUpdated } from '@components/FAQS';
import Footer from "@/pagecomponents/newcomponents/Footer";
import Navbar from "../newcomponents/Navbar";
import Link from "next/link";
import ContentReviewNote from '@components/ContentReviewNote';

const CoverLetterGenerator = dynamic(() => import('@components/CoverLetterGenerator'), { ssr: false })

const description = `Create an editable cover-letter draft based on your resume and a job description, then review and personalize it before applying.`;
const image = `https://production-appsyncstack-companyimages307b450c-17ekd4vq0gwy6.s3.us-west-2.amazonaws.com/lifeshack_opengraph.png`
const pageTitle = `AI Cover Letter Generator | Create Cover Letters Instantly`;

const CoverLetters = () => {
  return (
    <>
      <Head>
          <link
            href="https://fonts.googleapis.com/css?family=Roboto:100,100i,300,300i,400,400i,500,500i,700,700i,900,900i"
            rel="stylesheet"
          />
          <title>{pageTitle}</title>
          <meta name="description" content={description} />
          <link rel="canonical" href="https://www.lifeshack.com/ai-cover-letter/" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content="https://www.lifeshack.com/ai-cover-letter/" />
          <meta property="og:image" content={image} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={image} />     
      </Head>
      <Navbar />
      <section className="mx-auto max-w-4xl px-4 pb-4 pt-12">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900">
          AI Cover Letter Generator
        </h1>
        <p className="mt-3 text-lg text-gray-600">
          An AI cover letter generator matches verified experience from your resume to priorities in a job description
          and creates an editable first draft. Review the facts, add your voice, and personalize it before applying.
        </p>
      </section>
      <CoverLetterDirectAnswer />
      <ContentReviewNote />
      <CoverLetterGenerator showHeading={false} />
      <CoverLetterExample />
      <CoverLetterHowItWorks />
      <ProductResearchLinks />
      <FAQUpdated questionsAndAnswers={questionsAndAnswers} title={"Frequently Asked Questions on the AI Cover Letter Generator"} />
      <Footer />        
    </>
  )
};

function CoverLetterDirectAnswer() {
  return (
    <section className="mx-auto max-w-4xl px-4 py-8">
      <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-6 sm:p-8">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          What should an AI cover letter include?
        </h2>
        <p className="mt-4 text-lg leading-8 text-gray-700">
          A useful draft should name the role, connect one or two real qualifications to the employer&apos;s priorities,
          and explain why the opportunity interests you. It should not invent experience, repeat the resume word for
          word, or make claims you cannot support.
        </p>
      </div>
    </section>
  );
}

function CoverLetterExample() {
  return (
    <section className="bg-slate-50 px-6 py-16">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-indigo-700">Illustrative example</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
            Turn resume facts into a focused cover-letter opening
          </h2>
          <p className="mt-4 text-lg leading-8 text-gray-600">
            This example uses the same fictional candidate facts shown on the resume page. A useful draft draws
            a clear connection to the role without adding unsupported achievements.
          </p>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Inputs</h3>
            <ul className="mt-4 space-y-3 text-gray-700">
              <li><strong>Resume:</strong> customer questions by email and chat, billing-issue documentation, and weekly trend sharing.</li>
              <li><strong>Job description:</strong> customer communication, issue documentation, and cross-functional teamwork.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-indigo-300 bg-indigo-50 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Editable draft excerpt</h3>
            <blockquote className="mt-4 border-l-4 border-indigo-500 pl-4 leading-7 text-gray-800">
              I&apos;m interested in the Customer Success Specialist role because it combines direct customer support
              with the cross-functional problem solving I use in my current work. I answer account questions by email
              and chat, document recurring billing issues, and share weekly trends with product and billing teams.
            </blockquote>
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-5 text-gray-700">
          <strong className="text-gray-900">Before sending:</strong> confirm the company and role, replace generic
          enthusiasm with a genuine reason for applying, and verify every experience statement against your resume.
        </div>

        <p className="mt-8 text-lg text-gray-700">
          Need the source document first?{' '}
          <Link href="/ai-resume/">
            <a className="font-semibold text-indigo-700 underline decoration-indigo-300 underline-offset-4">
              Build a tailored resume with AI
            </a>
          </Link>{' '}
          before drafting the letter.
        </p>
      </div>
    </section>
  );
}

function CoverLetterHowItWorks() {
  return (
    <section className="relative isolate overflow-hidden bg-white">
      {/* subtle gradient to blend with hero */}
      <div className="pointer-events-none absolute inset-x-0 -top-20 -z-10 h-40 bg-gradient-to-b from-fuchsia-50/70 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
        {/* Lead block */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
            <span className="h-2 w-2 rounded-full bg-indigo-500" /> AI Cover Letter Generator
          </p>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Create a role-specific cover-letter draft
          </h2>
          <p className="mt-4 text-lg leading-7 text-gray-600">
            The generator uses the job description and your resume to create an editable starting point. Review every
            detail, add specific examples, and make the final letter sound like you before applying.
          </p>
        </div>

        {/* Divider */}
        <div className="mx-auto mt-10 h-px w-full max-w-5xl bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

        {/* How it works */}
        <div className="mx-auto mt-12 max-w-4xl text-center">
          <h3 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
            How to Create a Cover-Letter Draft with AI
          </h3>
          <p className="mt-3 text-gray-600">
            Our tool helps you overcome writer&apos;s block and the tedious task of personalizing letters. It’s more than a
            template; it&apos;s an intelligent AI cover letter writer that builds a narrative around your qualifications.
          </p>
        </div>

        <ol className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2">
          <Step
            number={1}
            title="Provide the Job and Your Resume"
            body={
              <>
                To begin, simply paste the text from the job description you&apos;re targeting into the tool. Then, confirm
                which version of your resume you&apos;d like to use. This context is all our AI needs to understand what the
                hiring manager is looking for and how your skills align.
              </>
            }
          />

          <Step
            number={2}
            title="The AI Analyzes, Writes, and Personalizes"
            body={
              <>
                The generator compares the job&apos;s key requirements with the experience in your resume. It creates an
                editable draft that highlights relevant skills and explains the connection between your experience and
                the role. Review and edit the draft before sending it.
              </>
            }
          />

          <Step
            number={3}
            title="Adjust the Tone to Match the Company"
            body={
              <>
                Guide the draft toward a professional, direct, enthusiastic, or concise style. Then edit the result so
                it reflects your natural voice rather than relying on the selected tone alone.
              </>
            }
          />

          <Step
            number={4}
            title="Edit, Verify, and Finalize the Letter"
            body={
              <>
                Treat the output as a starting point. Verify names and facts, remove generic phrases, add a specific
                reason for applying, and read the final version aloud before you send it.
              </>
            }
          />
        </ol>
      </div>
    </section>
  );
}

function Step({
  number,
  title,
  body,
}: {
  number: number;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <li className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-7">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
          <span className="text-base font-semibold">{number}</span>
        </div>
        <div>
          <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
          <p className="mt-2 text-gray-600 leading-relaxed">{body}</p>
        </div>
      </div>
    </li>
  );
}

export default CoverLetters;

function ProductResearchLinks() {
  return (
    <section className="bg-slate-50 px-6 py-14">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-3xl font-bold text-gray-900">
          Compare writing tools with full application workflows.
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          See when a cover-letter tool is enough and when an autofill,
          tracking, or cloud-agent product is the better fit.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <Link href="/ai-auto-apply/">
            <a className="font-semibold text-indigo-700">
              Use AI to apply for jobs automatically
            </a>
          </Link>
          <Link href="/ai-resume/">
            <a className="font-semibold text-indigo-700">
              Build an AI-tailored resume
            </a>
          </Link>
          <Link href="/best-ai-auto-apply-tools/">
            <a className="font-semibold text-indigo-700">
              Best AI auto-apply tools
            </a>
          </Link>
        </div>
      </div>
    </section>
  );
}

export const title = "Frequently Asked Questions on Our AI Cover Letter Generator";

export const questionsAndAnswers = [
  {
    question: "How does the AI personalize each cover letter?",
    answer:
      "The generator compares the requirements and responsibilities in the job description with the skills and experience in your resume. It uses those connections to create an editable draft, which you should verify and personalize before sending."
  },
  {
    question: "Will the cover letter sound like a robot wrote it?",
    answer:
      "The generator creates an editable first draft from your resume and the job description. Review it for accuracy, remove generic wording, and add your own voice and specific examples before sending."
  },
  {
    question: "What information does the AI need to write a letter?",
    answer:
      "Start with the job description and your current resume. Helpful optional details include the hiring manager's name, a genuine reason you want the role, a relevant example, the desired tone, and any point you want the letter to avoid."
  },
  {
    question: "How is this better than using a simple template?",
    answer:
      "A template provides structure, while an AI generator can draft sentences that connect your existing experience to the job description. The draft still needs your judgment, fact-checking, and personal voice."
  },
  {
    question: "Is the AI cover letter generator free?",
    answer:
      "You can create a LifeShack account without entering a credit card. Feature availability and usage limits depend on the current plan shown in the product."
  },
  {
    question: "Can I specify the tone of the cover letter?",
    answer:
      "Use your instructions and final edits to make the draft more formal, confident, enthusiastic, or concise. Always review the result so the tone matches the company and your own voice."
  },
  {
    question: "Can the AI invent details that are not on my resume?",
    answer:
      "Generated text can be inaccurate or infer more than your resume supports. Check every employer name, title, skill, example, and result. Delete or rewrite anything you cannot verify from your own experience."
  },
  {
    question: "Should I use the same cover letter for every application?",
    answer:
      "Reuse your core facts, but adjust the opening, most relevant example, and reason for applying for priority roles. If an employer does not request a cover letter, decide whether a concise, specific letter adds useful context rather than sending a generic one."
  }
];
